import { useState } from "react";
import { Edit2, Trash2, ToggleLeft, ToggleRight, Inbox, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import Spinner from "./Spinner";
import T from "../theme/colors";

function ActionIconBtn({ icon, hoverColor, hoverBg, onClick, title }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick} title={title}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? (hoverBg || `${hoverColor}12`) : "transparent",
        border: "none", cursor: "pointer", padding: "5px 6px", borderRadius: 5,
        color: hovered ? hoverColor : "var(--text-2)",
        display: "flex", alignItems: "center", transition: "all 0.15s",
      }}
    >
      {icon}
    </button>
  );
}

export function ActionBtn({ icon, color, tooltip, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick} title={tooltip}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? `${color}15` : "transparent",
        border: "none", cursor: "pointer", padding: "5px 6px", borderRadius: 5,
        display: "flex", alignItems: "center",
        color: hovered ? color : "var(--text-2)",
        transition: "all 0.15s",
      }}
    >
      {icon}
    </button>
  );
}

function Th({ children, width, sortKey, currentSortBy, currentSortDir, onSort }) {
  const sortable = !!(sortKey && onSort);
  const isActive = sortable && currentSortBy === sortKey;
  const [hovered, setHovered] = useState(false);
  return (
    <th
      onClick={sortable ? () => onSort(sortKey) : undefined}
      onMouseEnter={() => sortable && setHovered(true)}
      onMouseLeave={() => sortable && setHovered(false)}
      style={{
        textAlign: "left", padding: "9px 16px",
        color: isActive ? "var(--primary)" : "var(--text-2)",
        fontWeight: isActive ? 700 : 600, fontSize: 13,
        textTransform: "uppercase", letterSpacing: "0.07em",
        borderBottom: `1px solid var(--border)`,
        background: hovered ? "var(--bg-hover)" : "var(--bg-input)",
        whiteSpace: "nowrap",
        fontFamily: "var(--font-ui)",
        width: width || "auto",
        cursor: sortable ? "pointer" : "default",
        userSelect: "none",
        transition: "background 0.12s, color 0.12s",
      }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {children}
        {sortable && (
          isActive
            ? (currentSortDir === "asc"
              ? <ArrowUp size={11} style={{ color: "var(--primary)" }} />
              : <ArrowDown size={11} style={{ color: "var(--primary)" }} />)
            : <ArrowUpDown size={11} style={{ color: "var(--text-2)", opacity: 0.4 }} />
        )}
      </span>
    </th>
  );
}

function Td({ children, dim, wrap }) {
  return (
    <td style={{
      padding: "11px 16px",
      borderBottom: `1px solid var(--border)`,
      color: dim ? "var(--text-1)" : "var(--text-0)",
      fontSize: 15,
      whiteSpace: wrap ? "normal" : "nowrap",
      wordBreak: wrap ? "break-word" : undefined,
    }}>
      {children}
    </td>
  );
}

function TableRow({ row, columns, onEdit, onDelete, onToggle, extraActions, hasActions }) {
  const [hovered, setHovered] = useState(false);
  return (
    <tr
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ background: hovered ? "var(--bg-hover)" : "var(--bg-card)", transition: "background 0.12s" }}
    >
      {columns.map(col => (
        <Td key={col.key} dim={col.dim} wrap={col.wrap}>
          {col.render ? col.render(row[col.key], row) : (row[col.key] ?? <span style={{ color: "var(--text-2)" }}>—</span>)}
        </Td>
      ))}
      {hasActions && (
      <td style={{ padding: "8px 12px", borderBottom: `1px solid var(--border)` }}>
        <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
          {extraActions?.(row)}
          {onEdit && (
            <ActionIconBtn icon={<Edit2 size={13} />} hoverColor={"var(--info)"} title="Edit" onClick={() => onEdit(row)} />
          )}
          {onToggle && (
            <ActionIconBtn
              icon={row.isActive ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
              hoverColor={row.isActive ? "var(--warn)" : "var(--success)"}
              title={row.isActive ? "Deactivate" : "Activate"}
              onClick={() => onToggle(row)}
            />
          )}
          {onDelete && (
            <ActionIconBtn icon={<Trash2 size={13} />} hoverColor={"var(--danger)"} hoverBg={"var(--danger-bg)"} title="Delete" onClick={() => onDelete(row)} />
          )}
        </div>
      </td>
      )}
    </tr>
  );
}

const PAGE_SIZES = [10, 25, 50, 100];

/**
 * Phase 5 — Table component
 *
 * Two modes:
 *   1. Client mode (default): pass `data` (full array). Component handles
 *      pagination locally by slicing. Sorting is not done here in client
 *      mode — the consumer pre-sorts the data if needed.
 *   2. Server mode: set `serverMode` and pass page/size/sort state +
 *      callbacks. Pagination + sort are driven by the parent via
 *      useServerTable. Column-click on a column with `sortKey` triggers
 *      `onSortChange(sortKey)` which the parent uses to update state +
 *      re-fetch.
 *
 * Server-mode props (all required when serverMode is true):
 *   page, size, totalElements, sortBy, sortDir,
 *   onPageChange, onSizeChange, onSortChange
 */
export default function Table({
  columns, data, loading,
  onEdit, onDelete, onToggle, extraActions,
  emptyText = "No records found",
  defaultPageSize = 10,
  // ── server mode ──
  // Either pass `t` (the result of useServerTable) for shorthand,
  // OR pass the individual page/size/totalElements/sortBy/sortDir/onPageChange/etc. props.
  serverMode = false,
  t,                                  // shorthand: Table will read t.page, t.size, etc.
  page: serverPage,
  size: serverSize,
  totalElements,
  sortBy, sortDir,
  onPageChange,
  onSizeChange,
  onSortChange,
  // When `data` isn't given but t is, fall back to t.items.
  ...rest
}) {
  // If `t` shorthand is provided, derive missing props from it.
  // Explicit props still override (so consumers can mix).
  if (serverMode && t) {
    if (data === undefined) data = t.items;
    if (loading === undefined) loading = t.loading;
    if (serverPage === undefined) serverPage = t.page;
    if (serverSize === undefined) serverSize = t.size;
    if (totalElements === undefined) totalElements = t.totalElements;
    if (sortBy === undefined) sortBy = t.sortBy;
    if (sortDir === undefined) sortDir = t.sortDir;
    if (onPageChange === undefined) onPageChange = t.setPage;
    if (onSizeChange === undefined) onSizeChange = t.setSize;
    if (onSortChange === undefined) onSortChange = t.setSort;
  }
  // Phase 37 — only render the Actions column when the table actually has
  // an action. Read-only pages (e.g. DC Barcode Register) pass no
  // onEdit/onDelete/onToggle/extraActions, so the empty "Actions" column is
  // dropped for them without affecting tables that do use row actions.
  const hasActions = !!(onEdit || onDelete || onToggle || extraActions);
  // Client-mode local state — only used when serverMode is false
  const [clientPage, setClientPage] = useState(1);
  const [clientPageSize, setClientPageSize] = useState(defaultPageSize);

  const allRows = data || [];

  // Resolve the effective page / pageSize / total / pageRows depending on mode
  const page = serverMode ? (serverPage || 1) : clientPage;
  const pageSize = serverMode ? (serverSize || defaultPageSize) : clientPageSize;
  const total = serverMode ? (totalElements || 0) : allRows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, total);
  // In server mode the parent already gave us the current page's slice.
  const pageRows = serverMode ? allRows : allRows.slice(startIdx, endIdx);

  const setPage = serverMode ? (n) => onPageChange?.(n)
    : (n) => setClientPage(typeof n === "function" ? n(clientPage) : n);
  const setPageSize = serverMode ? (n) => onSizeChange?.(n)
    : (n) => { setClientPageSize(n); setClientPage(1); };

  // Page range buttons (max 5 shown)
  const pageNums = [];
  const delta = 2;
  for (let i = Math.max(1, safePage - delta); i <= Math.min(totalPages, safePage + delta); i++) {
    pageNums.push(i);
  }

  if (loading) return (
    <div style={{ padding: 48, display: "flex", justifyContent: "center" }}>
      <Spinner size={24} />
    </div>
  );

  return (
    <div>
      {/* ── Top bar: row count + page size + pagination ─────────────── */}
      {total > 0 && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "8px 16px",
          borderBottom: `1px solid var(--border)`,
          background: "var(--bg-input)",
          flexWrap: "wrap", gap: 10,
        }}>
          {/* Left: row count + page size selector */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 14, color: "var(--text-2)", fontFamily: "var(--font-ui)" }}>
              Showing{" "}
              <strong style={{ color: "var(--text-0)" }}>{startIdx + 1}–{endIdx}</strong>
              {" "}of{" "}
              <strong style={{ color: "var(--text-0)" }}>{total}</strong>
              {" "}records
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ fontSize: 13, color: "var(--text-2)", fontFamily: "var(--font-ui)" }}>Rows:</span>
              <select
                value={pageSize}
                onChange={e => setPageSize(Number(e.target.value))}
                style={{
                  fontSize: 14, color: "var(--text-0)", border: `1px solid var(--border)`,
                  borderRadius: 5, padding: "2px 6px", background: "var(--bg-card)",
                  cursor: "pointer", fontFamily: "var(--font-ui)", outline: "none",
                }}
              >
                {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Right: pagination buttons */}
          {totalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
              <PageBtn onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1}>
                <ChevronLeft size={13} />
              </PageBtn>
              {pageNums[0] > 1 && (
                <>
                  <PageBtn onClick={() => setPage(1)} active={safePage === 1}>1</PageBtn>
                  {pageNums[0] > 2 && <span style={{ fontSize: 14, color: "var(--text-2)", padding: "0 2px" }}>…</span>}
                </>
              )}
              {pageNums.map(n => (
                <PageBtn key={n} onClick={() => setPage(n)} active={safePage === n}>{n}</PageBtn>
              ))}
              {pageNums[pageNums.length - 1] < totalPages && (
                <>
                  {pageNums[pageNums.length - 1] < totalPages - 1 && (
                    <span style={{ fontSize: 14, color: "var(--text-2)", padding: "0 2px" }}>…</span>
                  )}
                  <PageBtn onClick={() => setPage(totalPages)} active={safePage === totalPages}>{totalPages}</PageBtn>
                </>
              )}
              <PageBtn onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>
                <ChevronRight size={13} />
              </PageBtn>
            </div>
          )}
        </div>
      )}

      <div style={{ overflowX: "auto" }}>
        <table style={{
          width: "100%", borderCollapse: "collapse",
          tableLayout: columns.some(c => c.width) ? "fixed" : "auto"
        }}>
          <thead>
            <tr>
              {columns.map(col => (
                <Th
                  key={col.key}
                  width={col.width}
                  sortKey={serverMode ? col.sortKey : undefined}
                  currentSortBy={sortBy}
                  currentSortDir={sortDir}
                  onSort={onSortChange}
                >
                  {col.label}
                </Th>
              ))}
              {hasActions && <Th width={columns.some(c => c.width) ? 90 : undefined}>Actions</Th>}
            </tr>
          </thead>
          <tbody>
            {pageRows.map(row => (
              <TableRow
                key={row.id ?? row.dcisId ?? `${row.dcNumber || "row"}-${row.barcode || ""}-${row.sizeValue || ""}`} row={row} columns={columns}
                onEdit={onEdit} onDelete={onDelete} onToggle={onToggle}
                extraActions={extraActions} hasActions={hasActions}
              />
            ))}
            {total === 0 && (
              <tr>
                <td colSpan={columns.length + (hasActions ? 1 : 0)} style={{ padding: "52px 16px", textAlign: "center", color: "var(--text-2)" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                    <Inbox size={28} style={{ opacity: 0.35 }} />
                    <span style={{ fontSize: 15 }}>{emptyText}</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PageBtn({ children, onClick, active, disabled }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        minWidth: 28, height: 28, padding: "0 6px",
        borderRadius: 6, border: `1px solid ${active ? "var(--primary)" : "var(--border)"}`,
        background: active ? "var(--primary)" : hovered && !disabled ? "var(--bg-hover)" : "var(--bg-card)",
        color: active ? "#fff" : disabled ? "var(--text-2)" : "var(--text-0)",
        fontSize: 14, fontWeight: active ? 700 : 400,
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.14s", fontFamily: "var(--font-ui)",
        opacity: disabled ? 0.45 : 1,
      }}
    >
      {children}
    </button>
  );
}